import { Link } from 'react-router-dom';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { TRAINING_SESSION_STATUS_LABEL, type TrainingSession } from '@allemnii-pulse/shared';
import { useIsMobile } from '../../lib/useIsMobile.js';
import { Card } from '../../components/ui/Card.js';

function dotClass(s: TrainingSession): string {
  if (s.stale) return 'bg-danger';
  if (s.status === 'ready') return 'bg-success';
  if (s.status === 'planned') return 'border-2 border-border bg-surface';
  return 'bg-accent';
}

function Point({ projectId, session }: { projectId: string; session: TrainingSession }) {
  return (
    <Link
      to={`/projects/${projectId}/sessions/${session.id}`}
      className="group flex flex-col gap-0.5"
    >
      <span className="text-[11px] font-medium text-muted">
        {session.date ? format(parseISO(session.date), 'EEE d MMM yyyy') : 'Not scheduled'}
      </span>
      <span className="break-words text-sm font-medium group-hover:text-accent">
        {session.order + 1}. {session.title}
      </span>
      <span className="text-[11px] text-muted">
        {session.durationMinutes} min ·{' '}
        {session.stale ? 'Needs review' : TRAINING_SESSION_STATUS_LABEL[session.status]}
      </span>
    </Link>
  );
}

/**
 * The sessions on a line, one point each, placed by their date — horizontally on wide screens
 * (proportional spacing, labels alternating above and below the line so neighbours don't
 * collide), vertically on phones. Sessions without a date are listed after the line.
 */
export function SessionsTimeline({
  projectId,
  sessions,
}: {
  projectId: string;
  sessions: TrainingSession[];
}) {
  const isMobile = useIsMobile();
  const dated = sessions
    .filter((s) => s.date)
    .sort((a, b) => a.date!.localeCompare(b.date!) || a.order - b.order);
  const undated = sessions.filter((s) => !s.date);

  if (sessions.length === 0)
    return <Card className="p-4 text-sm text-muted sm:p-6">No sessions yet.</Card>;

  return (
    <Card className="flex flex-col gap-6 p-4 sm:p-6">
      {dated.length === 0 ? (
        <p className="text-sm text-muted">Give the sessions dates to see them on the timeline.</p>
      ) : isMobile ? (
        <ol className="relative ml-2 flex flex-col gap-5 border-l-2 border-border pl-5">
          {dated.map((s) => (
            <li key={s.id} className="relative">
              <span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full ${dotClass(s)}`} />
              <Point projectId={projectId} session={s} />
            </li>
          ))}
        </ol>
      ) : (
        <HorizontalLine projectId={projectId} sessions={dated} />
      )}
      {undated.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-border pt-4">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">
            Not scheduled
          </span>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {undated.map((s) => (
              <Point key={s.id} projectId={projectId} session={s} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function HorizontalLine({
  projectId,
  sessions,
}: {
  projectId: string;
  sessions: TrainingSession[];
}) {
  const first = parseISO(sessions[0]!.date!);
  const span = Math.max(
    1,
    differenceInCalendarDays(parseISO(sessions[sessions.length - 1]!.date!), first),
  );
  // Proportional by date, but never closer than an even share allows, so same-day sessions
  // still get their own point.
  const minGap = sessions.length > 1 ? 100 / (sessions.length - 1) / 2 : 0;
  const positions: number[] = [];
  sessions.forEach((s, i) => {
    const byDate =
      sessions.length === 1
        ? 50
        : (differenceInCalendarDays(parseISO(s.date!), first) / span) * 100;
    positions.push(i === 0 ? byDate : Math.max(byDate, positions[i - 1]! + minGap));
  });
  const max = Math.max(100, positions[positions.length - 1] ?? 100);
  const scaled = positions.map((p) => (p / max) * 100);
  const width = Math.max(720, sessions.length * 170);

  return (
    <div className="overflow-x-auto pb-2">
      <div className="relative px-24" style={{ width }}>
        <div className="relative h-[260px]">
          <div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-border" />
          {sessions.map((s, i) => {
            const above = i % 2 === 0;
            return (
              <div key={s.id} className="absolute top-1/2" style={{ left: `${scaled[i]}%` }}>
                <span
                  className={`absolute -left-2 -top-2 h-4 w-4 rounded-full ring-4 ring-surface ${dotClass(s)}`}
                />
                <span
                  className={`absolute left-0 w-px bg-border ${above ? 'bottom-2 h-6' : 'top-2 h-6'}`}
                />
                <div
                  className={`absolute w-40 -translate-x-1/2 text-center ${above ? 'bottom-9' : 'top-9'}`}
                >
                  <Point projectId={projectId} session={s} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
